import { useEffect, useState } from "react";

type Json = Record<string, unknown>;

/**
 * Adding a passkey, shared by the tenant account page and the console profile (#354): the
 * creation options (behind a step-up), then the browser's ceremony and its registration.
 * Safari refuses `navigator.credentials.create` without a fresh click (transient activation),
 * so the ceremony runs only from the person's click: when the action runs again after a
 * step-up, it stops at the options and hands them to `ready`, and the page offers "Add the
 * passkey", whose click runs a new action with them (`prefetched`, no request before the
 * ceremony).
 *
 * Resolves true once registered, false when it stopped for a click.
 */
export function passkeyAddAction(steps: {
  options: () => Promise<Json>;
  /** The ceremony and its registration: called only within the click's run. */
  register: (options: Json) => Promise<void>;
  ready: (options: Json) => void;
  prefetched?: Json;
}): () => Promise<boolean> {
  let fromClick = true;
  return async () => {
    const direct = fromClick;
    fromClick = false;
    if (direct && steps.prefetched) {
      await steps.register(steps.prefetched);
      return true;
    }
    const options = await steps.options();
    if (!direct) {
      steps.ready(options);
      return false;
    }
    await steps.register(options);
    return true;
  };
}

/**
 * How long options a step-up fetched wait for the click: well inside the server's challenge
 * and step-up windows (5 minutes), after which "Add a passkey" starts over.
 */
export const PASSKEY_READY_MS = 120_000;

/**
 * A page's add state (#354): the options a step-up fetched, waiting for "Add the passkey". They
 * go after {@link PASSKEY_READY_MS}, and on `clear` (another passkey change consumes the
 * step-up), so the ceremony never runs on options the server no longer honours.
 */
export function usePasskeyAdd() {
  const [ready, setReady] = useState<Json | null>(null);
  useEffect(() => {
    if (!ready) {
      return;
    }
    const timer = window.setTimeout(() => setReady(null), PASSKEY_READY_MS);
    return () => window.clearTimeout(timer);
  }, [ready]);
  return {
    /** A step-up confirmed an add: the panel offers "Add the passkey". */
    ready: ready !== null,
    clear: () => setReady(null),
    /** The action of one click on the add button, with the waiting options if any. */
    action: (steps: {
      options: () => Promise<Json>;
      register: (options: Json) => Promise<void>;
    }) => {
      const prefetched = ready ?? undefined;
      setReady(null);
      return passkeyAddAction({ ...steps, ready: setReady, prefetched });
    },
  };
}
